import { Container, Graphics, Text, TextStyle, Texture } from 'pixi.js';
import gsap from 'gsap';
import type { ColorType, ClimberPose, MatchSide } from '@shared/types';
import type { EquippedSet } from '@shared/protocol';
import {
  STEPS_TOTAL,
  STEP_PX,
  LANE_H,
  HOLD_BAND_H,
  FOCUS_FROM_BOTTOM,
  HOLD_BASE_OFFSET,
  START_LINE_Y,
  DISRUPT_FLASH_MS,
  PATH_MAX_X,
} from '@shared/gameConfig';
import { PixiHold } from './Hold';
import { PixiClimber } from './Climber';
import { FinishBell } from './FinishBell';
import type { MatchBackgroundTheme } from '../matchBackground';

interface LaneOpts {
  side: MatchSide;
  width: number;
  color: 'red' | 'blue';
  textures: Record<ClimberPose, Texture>;
  seq: ColorType[];
  path: number[];
  /** Grayscale hold-shape skin tinted per color; null → built-in drawn arc. */
  holdTexture?: Texture | null;
  backgroundTheme?: MatchBackgroundTheme;
}

/**
 * A single climbing lane (granite wall + holds + climber). The hold band
 * scrolls upward as the climber progresses so the active hold stays
 * roughly `FOCUS_FROM_BOTTOM` from the lane bottom.
 */
export class Lane extends Container {
  readonly side: MatchSide;
  readonly width_: number;
  readonly color: 'red' | 'blue';

  private background: Graphics;
  private topMist: Graphics;
  private finishLine: Graphics;
  private finishLabel: Text;
  private mask_: Graphics;

  private holdBand: Container;
  private routeDotsLayer: Graphics;
  private holds: PixiHold[] = [];
  private holdTexture: Texture | null = null;
  private climber: PixiClimber;
  private bell: FinishBell | null = null;

  // Overlays
  private shieldAura: Graphics;
  private vortexOverlay: Container;

  // Tracked animations — must be killed in destroyLane to avoid GSAP
  // writing to Pixi properties of destroyed objects.
  private chev: Text | null = null;
  private chevTween: gsap.core.Tween | null = null;

  private seq: ColorType[];
  private path: number[];
  /** Per-hold Y position (cumulative pixels from wall bottom). Computed
   * deterministically from the path values so both clients see identical
   * spacing — some holds bunch up, others spread out. */
  private ys: number[] = [];
  private step = 0;
  private shieldVisible = false;
  private vortexT = 0;

  private targetScroll = 0;
  private currentScroll = 0;

  constructor(opts: LaneOpts) {
    super();
    this.side = opts.side;
    this.width_ = opts.width;
    this.color = opts.color;
    this.seq = opts.seq;
    this.path = opts.path;
    this.holdTexture = opts.holdTexture ?? null;
    // Uniform vertical spacing — each hold sits one climber-height (STEP_PX)
    // above the previous one. The variable-jitter variant looked organic but
    // broke the "climber sits exactly under the next hold" invariant the user
    // wanted; with uniform spacing the climber slot stays predictable.
    this.ys = this.path.map((_, i) => i * STEP_PX);

    // ── background granite gradient ────────────────────────────────
    this.background = new Graphics();
    this.drawBackground(opts.backgroundTheme);
    this.addChild(this.background);

    // hold band — moves up as step grows. y=0 at lane bottom.
    this.holdBand = new Container();
    this.holdBand.x = this.width_ / 2;
    this.holdBand.y = LANE_H;
    this.addChild(this.holdBand);

    // Mask the lane content to the lane rect (so the hold band doesn't
    // bleed outside while it scrolls).
    this.mask_ = new Graphics().rect(0, 0, this.width_, LANE_H).fill(0xffffff);
    this.addChild(this.mask_);
    this.mask = this.mask_;

    // route dots (drawn before holds so they sit underneath)
    this.routeDotsLayer = new Graphics();
    this.holdBand.addChild(this.routeDotsLayer);
    this.drawRouteDots();

    // Start line stripe
    this.drawStartLine();

    // Holds — server path values are in the logical range ±PATH_MAX_X.
    // Render them scaled to a fraction of the actual lane width so the
    // zig-zag amplitude scales with screen size. Y comes from the deterministic
    // jittered offsets so spacing varies — feels like a hand-drawn squiggle
    // instead of a metronome.
    const scale = this.scaleForPath();
    this.seq.forEach((c, i) => {
      const hold = new PixiHold(c, this.holdTexture);
      hold.x = (this.path[i] ?? 0) * scale;
      hold.y = -((this.ys[i] ?? i * STEP_PX) + HOLD_BASE_OFFSET);
      this.holdBand.addChild(hold);
      this.holds.push(hold);
    });

    // Climber
    this.climber = new PixiClimber({ textures: opts.textures, color: this.color, mirror: this.side === 'right' });
    this.holdBand.addChild(this.climber);
    this.positionClimber();

    // Finish bell — sits centered above the topmost hold. Triggered by
    // `triggerFinishBell()` when the climber reaches the last step.
    this.bell = new FinishBell();
    const total = this.seq.length;
    const topY = this.ys[total - 1] ?? (total - 1) * STEP_PX;
    this.bell.x = 0;
    this.bell.y = -(topY + HOLD_BASE_OFFSET + 60);
    this.holdBand.addChild(this.bell);

    // Top mist
    this.topMist = new Graphics();
    this.topMist.rect(0, 0, this.width_, 44).fill({
      color: 0x9aa4b8,
      alpha: 0.25,
    });
    this.addChild(this.topMist);

    // Finish line at the top
    this.finishLine = new Graphics();
    const stripes = Math.ceil(this.width_ / 12);
    for (let s = 0; s < stripes; s++) {
      const fill = s % 2 === 0 ? 0xf4c724 : 0x1e1b4b;
      this.finishLine.rect(s * 12, 28, 6, 6).fill(fill);
    }
    this.addChild(this.finishLine);

    this.finishLabel = new Text({
      text: 'FINISH',
      style: new TextStyle({
        fontFamily: 'Bungee, Impact',
        fontSize: 9,
        fill: 0xf4c724,
        letterSpacing: 1.4,
        dropShadow: {
          color: 0xf4c724,
          alpha: 0.6,
          blur: 4,
          distance: 0,
        },
      }),
    });
    this.finishLabel.anchor.set(0.5, 0);
    this.finishLabel.x = this.width_ / 2;
    this.finishLabel.y = 10;
    this.addChild(this.finishLabel);

    // Shield aura — attached to the holdBand so it scrolls with the climber.
    this.shieldAura = new Graphics();
    this.shieldAura.visible = false;
    this.holdBand.addChild(this.shieldAura);

    // Vortex overlay (a stack of conic-style arcs)
    this.vortexOverlay = new Container();
    this.vortexOverlay.visible = false;
    this.vortexOverlay.x = this.width_ / 2;
    this.vortexOverlay.y = LANE_H * 0.35;
    this.addChild(this.vortexOverlay);
    this.drawVortex();
  }

  private drawBackground(theme?: MatchBackgroundTheme): void {
    const g = this.background;
    const w = this.width_;
    const h = LANE_H;

    // Approximate the prototype's granite by stacking 4 horizontal bands
    // with the same colors the CSS uses. Cheap, no shader needed.
    const colors = theme?.laneColors ?? [0x5a5358, 0x3d3739, 0x2a2528, 0x1c1a1d];
    // Custom arena art is rendered by the CSS layer directly behind Pixi.
    // Keep just enough wall tint here for hold/climber contrast while letting
    // that player's purchased artwork remain clearly visible.
    const opacity = theme?.isCustom
      ? Math.min(0.32, theme.laneOpacity)
      : theme?.laneOpacity ?? 1;
    const bands = [
      { y: 0, h: h * 0.35, c: colors[0] },
      { y: h * 0.35, h: h * 0.35, c: colors[1] },
      { y: h * 0.7, h: h * 0.2, c: colors[2] },
      { y: h * 0.9, h: h * 0.1, c: colors[3] },
    ];
    bands.forEach((b) => g.rect(0, b.y, w, b.h).fill({ color: b.c, alpha: opacity }));

    // top sun-lit highlight
    g.ellipse(w / 2, 0, w * 0.6, h * 0.18).fill({ color: 0xffffff, alpha: 0.06 });

    // diagonal cracks
    g.poly([w * 0.2, 0, w * 0.25, 0, w * 0.55, h, w * 0.5, h]).fill({
      color: 0x000000,
      alpha: 0.18,
    });
    g.poly([w * 0.65, h * 0.2, w * 0.68, h * 0.2, w * 0.85, h * 0.9, w * 0.82, h * 0.9]).fill({
      color: 0x000000,
      alpha: 0.12,
    });

    // sparse speckle (random but deterministic-ish positions)
    const seedPoints: Array<[number, number, number, number]> = [
      [0.18, 0.22, 1.2, 0.18],
      [0.64, 0.38, 1, 0.12],
      [0.34, 0.7, 1.2, -0.3],
      [0.82, 0.8, 1, -0.25],
      [0.08, 0.55, 0.9, 0.1],
      [0.92, 0.12, 1, -0.3],
      [0.5, 0.45, 0.8, 0.15],
      [0.22, 0.85, 0.8, -0.2],
      [0.7, 0.6, 1.1, 0.16],
    ];
    seedPoints.forEach(([fx, fy, r, alpha]) => {
      g.circle(w * fx, h * fy, r).fill({
        color: alpha > 0 ? 0xffffff : 0x000000,
        alpha: Math.abs(alpha),
      });
    });

    // rounded inset stroke
    g.rect(0, 0, w, h).stroke({ color: 0x000000, alpha: 0.4, width: 1, alignment: 1 });
  }

  /** Play the finish-line bell strike. Called when the climber arrives at
   * the top — the lane's own buzzer (the real World Games speed-climb prop).
   * Idempotent so repeated MATCH_OVER events don't re-trigger. */
  triggerFinishBell(sound = true): void {
    this.bell?.triggerSlam({ sound });
  }

  /** Apply the equipped Character skin (pose PNGs + tint) to this climber. */
  setCharacter(skin: import('@shared/protocol').CharacterSkin | null): void {
    this.climber.setCharacterSkin(skin);
  }

  /** Apply a hold-shape skin texture to every hold in this lane (or null to
   * revert to the built-in drawn arc). */
  setHoldTexture(tex: Texture | null): void {
    this.holdTexture = tex;
    this.holds.forEach((h) => h.setShapeTexture(tex));
  }

  /** Winner's victory stomp. `onImpact` fires at the slam frame. */
  playWinnerStomp(onImpact?: () => void): void {
    this.climber.playWinStomp(onImpact);
  }

  /** Loser gets knocked down the wall. */
  playLoserKnockdown(): void {
    this.climber.playKnockedDown();
  }

  /** World-space position of the climber's feet (for FX placement). Reuses
   * the FX anchor — already lane-local + scroll-aware — plus the lane offset. */
  getClimberWorldPos(): { x: number; y: number } {
    const a = this.getFxAnchor();
    return { x: this.x + a.x, y: this.y + a.y };
  }

  /** Scale factor to map server path values (±PATH_MAX_X) to screen pixels.
   * Target swing = laneWidth / 4 on each side, so the zig-zag amplitude
   * always matches the lane regardless of phone size. Clamped to keep a
   * minimum so holds don't pile up on the centerline at very narrow widths. */
  private scaleForPath(): number {
    const target = this.width_ / 4;
    return Math.max(8, target) / PATH_MAX_X;
  }

  private drawStartLine(): void {
    // Diagonal yellow/navy stripes at the lane bottom, plus a START badge.
    const startLine = new Graphics();
    startLine.x = -this.width_ / 2; // back to lane-left
    startLine.y = -START_LINE_Y; // 10px above lane bottom
    const stripeH = 18;
    const w = this.width_;
    for (let i = -w; i < w * 2; i += 12) {
      startLine.poly([i, 0, i + 6, 0, i - stripeH + 6, stripeH, i - stripeH, stripeH]).fill(0xf4c724);
    }
    // navy in between
    startLine.rect(0, 0, w, stripeH).fill({ color: 0x1e1b4b, alpha: 0.35 });
    startLine.rect(0, 0, w, 2).fill(0xf4c724);
    startLine.rect(0, stripeH - 2, w, 2).fill(0xf4c724);
    this.holdBand.addChild(startLine);

    // START badge
    const badge = new Container();
    badge.x = 0;
    badge.y = -6;
    const bgGfx = new Graphics()
      .roundRect(-32, -10, 64, 20, 6)
      .fill(0xf4c724)
      .stroke({ color: 0xffffff, width: 1.5 });
    badge.addChild(bgGfx);
    const label = new Text({
      text: '★ START',
      style: new TextStyle({
        fontFamily: 'Bungee, Impact',
        fontSize: 10,
        letterSpacing: 2,
        fill: 0x1e1b4b,
      }),
    });
    label.anchor.set(0.5);
    badge.addChild(label);
    this.holdBand.addChild(badge);

    // Up chevron above — track on the instance so destroyLane can kill the
    // tween cleanly. Otherwise the tween outlives the Text node and gsap
    // tries to set .y on a destroyed Pixi object → "Cannot set properties
    // of null" crash inside the Pixi ticker.
    const chev = new Text({
      text: '▲',
      style: new TextStyle({
        fontFamily: 'Bungee, Impact',
        fontSize: 14,
        fill: 0xf4c724,
        letterSpacing: 5.6,
      }),
    });
    chev.anchor.set(0.5);
    chev.y = -28;
    chev.alpha = 0.7;
    this.holdBand.addChild(chev);
    this.chev = chev;
    this.chevTween = gsap.to(chev, {
      y: -34,
      duration: 0.7,
      yoyo: true,
      repeat: -1,
      ease: 'sine.inOut',
    });
  }

  private drawRouteDots(): void {
    const g = this.routeDotsLayer;
    g.clear();
    const scale = this.scaleForPath();
    for (let i = 0; i < this.path.length - 1; i++) {
      const x1 = this.path[i]! * scale;
      const x2 = this.path[i + 1]! * scale;
      const y1 = -((this.ys[i] ?? i * STEP_PX) + HOLD_BASE_OFFSET + 8);
      const y2 = -((this.ys[i + 1] ?? (i + 1) * STEP_PX) + HOLD_BASE_OFFSET + 8);
      [0.33, 0.66].forEach((t) => {
        const x = x1 + (x2 - x1) * t;
        const y = y1 + (y2 - y1) * t;
        g.circle(x, y, 1.5).fill({ color: 0xf4c724, alpha: 0.5 });
      });
    }
  }

  private drawVortex(): void {
    const c = this.vortexOverlay;
    c.removeChildren();
    const colors = [0x9b5de5, 0xe63946, 0xf4c724, 0x3b82f6];
    colors.forEach((col, i) => {
      const arc = new Graphics();
      arc.moveTo(0, 0);
      arc.arc(0, 0, 80, (i * Math.PI) / 2, (i + 1) * (Math.PI / 2)).lineTo(0, 0);
      arc.fill({ color: col, alpha: 0.45 });
      c.addChild(arc);
    });
    // central glow
    const core = new Graphics().circle(0, 0, 14).fill({ color: 0xffffff, alpha: 0.85 });
    c.addChild(core);
  }

  private positionClimber(): void {
    const { x, y } = this.climberAnchor(this.step);
    gsap.killTweensOf(this.climber);
    gsap.to(this.climber, {
      x, y,
      duration: 0.55,
      ease: 'back.out(1.5)',
    });
  }

  /** Where the climber should sit at a given step. Always BELOW the next
   * hold (step index = `step` since we just climbed `step-1` holds), so the
   * player can read the target color as "the hold right above me". The x
   * also follows the next hold so the climber visibly traverses sideways
   * before grabbing.
   *
   * Edge cases:
   *  - step === 0: hovering below hold[0], the very first target.
   *  - step === total: clamped to last hold (no more to jump to).
   */
  private climberAnchor(step: number): { x: number; y: number } {
    const total = this.path.length;
    const nextIdx = Math.max(0, Math.min(step, total - 1));
    const rawX = this.path[nextIdx] ?? 0;
    const x = rawX * this.scaleForPath();
    const targetHoldY = (this.ys[nextIdx] ?? nextIdx * STEP_PX) + HOLD_BASE_OFFSET;
    // Climber's bottom anchor sits exactly STEP_PX below the target hold so
    // the sprite slots into the previous step's "rung". When step === total
    // (climbed everything), place the climber AT the top hold.
    const offsetBelow = step >= total ? 0 : STEP_PX;
    const y = -(targetHoldY - offsetBelow);
    return { x, y };
  }

  setStep(step: number, force = false): void {
    // `force` re-applies the position even when the logical step hasn't
    // changed — used after a scene rebuild (e.g. rematch) where the new
    // climber instance defaults to step 0 and the store also says step 0,
    // but we still need to place the sprite at the correct path[0] coords.
    if (step === this.step && !force) return;
    const wasHigher = step > this.step;
    this.step = step;

    this.holds.forEach((h, i) => {
      if (i < step) h.setState('past');
      else if (i === step) h.setState('current');
      else h.setState('future');
    });

    // Position climber under the NEXT hold (the one to grab).
    const { x: targetX, y: targetY } = this.climberAnchor(step);
    const targetIdx = Math.max(0, Math.min(step, this.path.length - 1));
    const targetYpx = this.ys[targetIdx] ?? targetIdx * STEP_PX;

    // Smooth, no-overshoot motion. The old `back.out(1.5)` looked snappy in
    // isolation but combined with the climb sprite hop and the lane scroll it
    // read as "jumpy". `power3.out` (or sine for downward) glides instead.
    gsap.killTweensOf(this.climber);
    gsap.to(this.climber, {
      x: targetX,
      y: targetY,
      duration: wasHigher ? 0.42 : 0.55,
      ease: wasHigher ? 'power3.out' : 'sine.in',
    });

    // Scroll target — keep the climber roughly FOCUS_FROM_BOTTOM from the
    // lane floor for most of the race. Final-stretch behavior: once the LAST
    // hold can fit on-screen under the FINISH line, stop scrolling. The
    // climber then visibly RUNS UP to the finish (instead of hovering in the
    // middle while the wall slides past).
    const climberY = step === 0 ? START_LINE_Y + 4 : targetYpx + HOLD_BASE_OFFSET + 4;
    const rawTarget = Math.max(0, climberY - (LANE_H - FOCUS_FROM_BOTTOM - STEP_PX));
    const total = this.seq.length;
    // Space we want to keep reserved at the top of the lane for the FINISH
    // band + label.
    const FINISH_RESERVE = 48;
    const lastHoldY = (this.ys[total - 1] ?? (total - 1) * STEP_PX) + HOLD_BASE_OFFSET;
    // Scroll value that pins the last hold just below FINISH_RESERVE.
    const maxScroll = Math.max(0, lastHoldY - LANE_H + FINISH_RESERVE + STEP_PX);
    this.targetScroll = Math.min(maxScroll, rawTarget);
  }

  /** Hot-swap climber sprite textures once the real PNGs finish loading. */
  setTextures(textures: Record<ClimberPose, Texture>): void {
    this.climber.setTextures(textures);
  }

  setPose(pose: ClimberPose, textures: Record<ClimberPose, Texture>): void {
    this.climber.setPose(pose, textures);
  }

  setEquipped(equipped: EquippedSet | null): void {
    this.climber.setEquipped(equipped);
  }

  setShield(active: boolean): void {
    if (this.shieldVisible === active) return;
    this.shieldVisible = active;
    if (active) {
      // Draw the aura around 0,0 (climber anchor); position is updated each
      // frame in update() so it follows the climber up the wall.
      this.shieldAura.visible = true;
      this.shieldAura.clear();
      this.shieldAura
        .ellipse(0, -30, 42, 60)
        .stroke({ color: 0x22d3ee, width: 2.5, alpha: 0.9 })
        .fill({ color: 0x22d3ee, alpha: 0.12 });
      this.syncShieldPosition();
      gsap.fromTo(
        this.shieldAura.scale,
        { x: 0.92, y: 0.92 },
        { x: 1.1, y: 1.1, duration: 0.6, yoyo: true, repeat: -1, ease: 'sine.inOut' }
      );
      gsap.fromTo(
        this.shieldAura,
        { alpha: 0.5 },
        { alpha: 0.95, duration: 0.6, yoyo: true, repeat: -1, ease: 'sine.inOut' }
      );
    } else {
      gsap.killTweensOf(this.shieldAura.scale);
      gsap.killTweensOf(this.shieldAura);
      this.shieldAura.scale.set(1);
      this.shieldAura.alpha = 1;
      this.shieldAura.visible = false;
    }
  }

  private syncShieldPosition(): void {
    if (!this.shieldVisible) return;
    this.shieldAura.x = this.climber.x;
    this.shieldAura.y = this.climber.y;
  }

  /**
   * Strobe the holds whose colors are about to be reshuffled by an incoming
   * Disrupt. Pass an empty array to clear. The flash duration matches
   * DISRUPT_FLASH_MS so the swap timer and visual stay in lockstep.
   */
  setDisruptingIndices(indices: number[]): void {
    // Reset any previously flashing holds first.
    this.holds.forEach((h) => h.stopFlash());
    if (!indices.length) return;
    indices.forEach((i) => {
      const h = this.holds[i];
      h?.startFlash(DISRUPT_FLASH_MS);
    });
  }

  setVortex(active: boolean): void {
    if (active === this.vortexOverlay.visible) return;
    this.vortexOverlay.visible = active;
    if (active) {
      this.vortexT = 0;
      this.vortexOverlay.alpha = 0.9;
      this.vortexOverlay.scale.set(1);
      gsap.to(this.vortexOverlay, {
        alpha: 0,
        duration: 1.2,
        ease: 'power2.out',
        onComplete: () => {
          this.vortexOverlay.visible = false;
        },
      });
      gsap.to(this.vortexOverlay.scale, {
        x: 1.4,
        y: 1.4,
        duration: 1.2,
        ease: 'power2.out',
      });
    }
  }

  /**
   * Repaint holds after a Disrupt reshuffle. We RECOLOR in place rather than
   * destroy + recreate — destroying a PixiHold that's mid-flash races against
   * its own GSAP tween and crashed the opponent's scene.
   */
  updateSeq(seq: ColorType[]): void {
    this.seq = seq;
    seq.forEach((c, i) => {
      this.holds[i]?.recolor(c);
    });
  }

  update(dt: number): void {
    // Smoothly lerp scroll towards target. Higher k = scroll catches up faster
    // so it matches the climber's tween instead of lagging behind and creating
    // a "stepped" visual.
    const k = Math.min(1, dt * 6);
    this.currentScroll += (this.targetScroll - this.currentScroll) * k;
    this.holdBand.y = LANE_H + this.currentScroll;

    if (this.vortexOverlay.visible) {
      this.vortexT += dt;
      this.vortexOverlay.rotation = this.vortexT * 12;
    }

    this.holds.forEach((h) => h.update(dt));

    // Shield aura tracks the climber as it tweens between steps.
    this.syncShieldPosition();
  }

  destroyLane(): void {
    gsap.killTweensOf(this.climber);
    gsap.killTweensOf(this.shieldAura);
    gsap.killTweensOf(this.vortexOverlay);
    this.chevTween?.kill();
    this.chevTween = null;
    if (this.chev) gsap.killTweensOf(this.chev);
    this.chev = null;
    // Holds may have flash tweens running (Disrupt) — make sure we wipe them
    // before super.destroy kills the underlying Pixi objects.
    this.holds.forEach((h) => h.stopFlash());
    this.climber.destroyClimber();
    this.destroy({ children: true });
  }

  /** Lane-relative coords where bursts/floaty text should appear. */
  getFxAnchor(): { x: number; y: number } {
    return { x: this.width_ / 2, y: LANE_H - FOCUS_FROM_BOTTOM };
  }

  // Static helpers
  static maxSteps(): number {
    return STEPS_TOTAL;
  }
  static bandHeight(): number {
    return HOLD_BAND_H;
  }
}
